import React, { useState, useEffect, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import { createPDF } from 'utils/createPDF';
import { CloudDone } from '@mui/icons-material';
import { Avatar, Box, CircularProgress, Skeleton, Typography } from '@mui/material';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
/**
 * ResumeBuilder is a React component that renders a PDF viewer for a user's resume.
 * It takes the user's profile, education, work experience, certificates, custom section, summary, and finance technologies as props.
 * It uses the createPDF utility function to generate a PDF buffer from the provided data.
 * It also uses the PDFViewer component to display the PDF document.
 * If there is an error, it logs the error and sets the loading state to false.
 * @param {Object} profile - User profile data.
 * @param {Array} educationData - Array of education objects.
 * @param {Array} workExperienceData - Array of work experience objects.
 * @param {Array} certificateData - Array of certificate objects.
 * @param {Array} customSection - Array of custom section objects.
 * @param {String} summaryData - Summary text.
 * @param {Array} financeTechologiesData - Array of finance technologies objects.
 */
const ResumeBuilder = ({ updating, setUpdating, setResumePDF, profile, educationData, workExperienceData, certificateData, customSection, summaryData, financeTechologiesData }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const canvas1Ref = useRef(null);
  const canvas2Ref = useRef(null);
  const [currentCanvas, setCurrentCanvas] = useState(1);
  const [thumbnails, setThumbnails] = useState([]);
  const [isRendering, setIsRendering] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [bgColor, setBgColor] = useState("#fff");

  useEffect(() => {
    const timer = setTimeout(() => {
      setBgColor(pdfDoc ? "transparent" : "#fff");
    }, 300);

    return () => clearTimeout(timer); // Cleanup timeout
  }, [pdfDoc]); // Runs when `pdfDoc` changes

  // Effect to load the PDF when pdfBuffer changes
  useEffect(() => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const newTimeout = setTimeout(() => {
      generatePdfBuffer();
    }, 50);

    setDebounceTimeout(newTimeout);
  }, [profile, educationData, workExperienceData, certificateData, customSection, summaryData, financeTechologiesData]);

  /**
   * Generates a PDF buffer using the provided profile, education, work experience, certificates, custom section, summary, and finance technologies.
   * It uses the createPDF utility function to generate a PDF buffer from the provided data.
   * It then converts the buffer to a blob, sets it to the component's state as pdfDoc, and loads the PDF document into the PDFViewer component.
   * If there is an error, it logs the error and sets the loading state to false.
   */
  const generatePdfBuffer = async () => {
    try {
      const buffer = await createPDF({
        profile,
        educationData, workExperienceData, certificateData, customSection,
        summaryData,
        financeTechologiesData
      });
      const blob = new Blob([buffer], { type: 'application/pdf' });
      setResumePDF(blob);
      await loadPdfDocument(buffer);
    } catch (e) {
      setUpdating(false);
    }
  };


  /**
   * Loads a PDF document into the component's state from the provided PDF array.
   * @param {ArrayBuffer} pdfArray - The PDF document as an ArrayBuffer.
   * @returns {Promise<void>}
   */
  const loadPdfDocument = async (pdfArray) => {
    const loadingTask = pdfjs.getDocument({ data: pdfArray });
    const loadedPdfDoc = await loadingTask.promise;
    setPdfDoc(loadedPdfDoc);
    setTotalPages(loadedPdfDoc.numPages);
    generateThumbnails(loadedPdfDoc);
    setTimeout(() => setUpdating(false), 1500);
  };

  /**
   * Renders a page of the PDF document into the canvas.
   * @param {number} pageNum - The page number to render.
   * @returns {Promise<void>}
   */
  const renderPage = async (pageNum) => {
    if (!pdfDoc) return;

    const canvasToUse = currentCanvas === 1 ? canvas1Ref.current : canvas2Ref.current;
    const ctx = canvasToUse.getContext('2d');

    ctx.clearRect(0, 0, canvasToUse.width, canvasToUse.height);

    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 });

    canvasToUse.width = viewport.width;
    canvasToUse.height = viewport.height;

    const renderContext = {
      canvasContext: ctx,
      viewport,
    };

    await page.render(renderContext).promise;

    setCurrentCanvas(currentCanvas === 1 ? 2 : 1);

  };

  /**
   * Generates an array of thumbnails for the pages of the provided PDF document.
   * Thumbnails are rendered at a scale of 0.2 and are stored in an array
   * with the page number as the key.
   * The generated thumbnails are then stored in the component's state.
   * @param {pdfjs.PDFDocument} pdfDoc - The PDF document to generate thumbnails from.
   * @returns {Promise<void>}
   */
  const generateThumbnails = async (pdfDoc) => {
    const thumbnails = [];
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const scale = 0.2; // Thumbnail scale factor
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: ctx,
        viewport,
      };

      await page.render(renderContext).promise;
      thumbnails.push({ pageNum: i, dataUrl: canvas.toDataURL() });
    }
    setThumbnails(thumbnails);
  };

  useEffect(() => {
    if (pdfDoc) {
      renderPage(currentPage);
    }
  }, [currentPage, pdfDoc]);

  /**
   * Handles the change event for the input fields in the form.
   * Clears any existing debounce timeout, and then sets a new timeout
   * to call generatePdfBuffer after 500ms.
   * @param {Event} event - The event that triggered the change.
   */
  const handleChange = (event) => {
    const userData = {
      name: document.getElementById('name').value,
      email: document.getElementById('email').value,
      experience: document.getElementById('experience').value,
    };

    if (debounceTimeout) clearTimeout(debounceTimeout);

    const newTimeout = setTimeout(() => {
      generatePdfBuffer(userData);
    }, 500);

    setDebounceTimeout(newTimeout);
  };

  /**
   * Handles the click event on a thumbnail to navigate
   * to the corresponding page in the PDF document.
   * Updates the current page number in the state.
   * @param {number} pageNum - The page number to navigate to.
   */

  const handleThumbnailClick = (pageNum) => {
    setCurrentPage(pageNum);
    // setCurrentCanvas(currentCanvas === 1 ? 2 : 1);
  };

  /**
   * Handles the click event on the "Next Page" button.
   * Increments the current page number in the state if it is not
   * already at the last page.
   */
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle Previous Page
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentCanvas(currentCanvas === 1 ? 2 : 1);
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="editor-view">
      <Box className="pdf-view-wrapper">
        <div style={{ display: 'inline-block', position: 'relative', width: '75%' }}>
          <Box className='pdf-container' sx={{ backgroundColor: `${bgColor} !important` }}>
            {!pdfDoc ?
              <Box sx={{ display: "contents", position: "relative" }} >
                <div style={{ margin: '30px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ margin: 1 }}>
                      <Skeleton variant="circular">
                        <Avatar />
                      </Skeleton>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <Typography><Skeleton width="40%"></Skeleton></Typography>
                      <Typography variant="body"><Skeleton width="50%"></Skeleton></Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="h5"><Skeleton width="100%"></Skeleton></Typography>
                    <Typography variant="body"><Skeleton width="100%"></Skeleton></Typography>


                    <Typography variant="h5"><Skeleton width="100%"></Skeleton></Typography>
                    <Typography variant="body"><Skeleton width="100%"></Skeleton></Typography>

                    <Typography variant="h5"><Skeleton width="100%"></Skeleton></Typography>
                    <Typography variant="body"><Skeleton width="100%"></Skeleton></Typography>

                    <Typography variant="h5"><Skeleton width="100%"></Skeleton></Typography>
                    <Typography variant="body"><Skeleton width="100%"></Skeleton></Typography>
                  </Box>
                </div>
                <canvas sx={{ visibilty: 'hidden' }}
                  style={{
                    objectFit: 'contain',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }} ></canvas>
              </Box>
              : <Box sx={{ display: "contents", position: "relative" }}>
                {/* Canvas 1 */}
                <canvas
                  className={currentCanvas === 2 ? 'pdf-fade-in' : 'pdf-fade-out'}
                  ref={canvas1Ref}
                  style={{
                    objectFit: 'contain',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }}
                ></canvas>

                {/* Canvas 2 */}
                <canvas
                  className={currentCanvas === 1 ? 'pdf-fade-in' : 'pdf-fade-out'}
                  ref={canvas2Ref}
                  style={{
                    objectFit: 'contain',
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0
                  }}
                ></canvas>
              </Box>}
          </Box>
          <div className="pdf-nav-block">
            {pdfDoc ? <div style={{ position: 'absolute', left: '0px' }}>
              <div className="rendering-status">
                {(updating ?
                  <Box display="flex" flexDirection="row" ><CircularProgress size="sm" sx={{ color: 'white', width: 15, height: 15, marginRight: '5px' }} /> Saving...</Box>
                  : <Box display="flex" flexDirection="row"><CloudDone className='status-icon' /> Saved</Box>)}
              </div>
            </div> : null}
            {totalPages ? <div className="page-nav">
              <div className={`prev-icon ${currentPage === 1 ? 'disabled' : ''}`} onClick={handlePrevPage}>
                <svg
                  fill="#fff"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.32427537,7.23715414 L10.6757246,5.76284586 L16.6757246,11.2628459 C17.1080918,11.6591824 17.1080918,12.3408176 16.6757246,12.7371541 L10.6757246,18.2371541 L9.32427537,16.7628459 L14.5201072,12 L9.32427537,7.23715414 Z"></path>
                </svg>
              </div>
              <div className="page-count">{currentPage} / {totalPages}</div>
              <div className={`next-icon ${currentPage === totalPages ? 'disabled' : ''}`} onClick={handleNextPage}>
                <svg
                  fill="#fff"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.32427537,7.23715414 L10.6757246,5.76284586 L16.6757246,11.2628459 C17.1080918,11.6591824 17.1080918,12.3408176 16.6757246,12.7371541 L10.6757246,18.2371541 L9.32427537,16.7628459 L14.5201072,12 L9.32427537,7.23715414 Z"></path>
                </svg>
              </div>
            </div> : null}
          </div>
        </div>
      </Box>

    </div>
  );
};

export default ResumeBuilder;
